import type { ChartWrapperOptions } from 'react-google-charts';
import type { GetDashboardInitialData } from 'lib/queries/dashboard';

import React, { memo, useEffect, useMemo, useRef, useState } from 'react';
import { Chart } from 'react-google-charts';
import { useResizeObserver } from '@leafwell/components';

export type StudiesByYearChartProps = {
  studies: GetDashboardInitialData['studies']['byYear'];
};

const SVGElement: React.FC = memo(() => {
  return (
    <svg className="w-0 h-0 absolute" aria-hidden="true" focusable="false">
      <defs>
        <linearGradient id="chart-gradient" gradientTransform="rotate(90)">
          <stop offset="0%" stopColor="#CEB9F3" stopOpacity="1" />
          <stop offset="100%" stopColor="white" stopOpacity="1" />
        </linearGradient>
      </defs>
    </svg>
  );
});

const FILL_COLOR = '#9A6CEA';

const chartOptions: ChartWrapperOptions['options'] = {
  backgroundColor: 'none',
  colors: [FILL_COLOR],
  hAxis: {
    baselineColor: 'none',
    gridlines: {
      color: 'none',
    },
    format: '#',
    slantedText: false,
  },
  vAxis: {
    baselineColor: 'none',
    gridlines: {
      color: 'none',
    },
    format: '#',
    viewWindow: { min: 0 },
  },
};

const StudiesByYearChart: React.FC<StudiesByYearChartProps> = ({ studies }) => {
  const [{ height, width }, setDimensions] = useState({ height: 0, width: 0 });
  const chartContainer = useRef(null);

  const data = useMemo(() => {
    const groupedByYear = studies.reduce((prev, curr) => {
      return {
        ...prev,
        [curr['year']]: (prev[curr['year']] || []).concat(curr),
      };
    }, {});

    const chartData = [['Year', 'Studies']];

    Object.keys(groupedByYear).map(key =>
      chartData.push([parseInt(key), groupedByYear[key].length]),
    );

    return chartData;
  }, [studies]);

  useResizeObserver(entry => {
    setDimensions({
      height: entry?.contentRect?.height || 0,
      width: entry?.contentRect?.width || 0,
    });
  }, chartContainer);

  const options = useMemo(
    () => ({
      ...chartOptions,
      areaOpacity: 1,
      chartArea: {
        left: 60,
        height: '80%',
        width: width - 80,
      },
      legend: { position: 'none' },
      height: height * 0.75,
      width,
    }),
    [height, width],
  );

  useEffect(() => {
    if (chartContainer.current) {
      const styleSheet = document.createElement('style');

      styleSheet.innerText = `.leafwell-area-chart [fill="${FILL_COLOR}"],.leafwell-area-chart [fill="${FILL_COLOR.toLowerCase()}"] { fill: url(#chart-gradient) !important;}`;

      chartContainer.current.appendChild(styleSheet);
    }
  }, [chartContainer]);

  // do not start the chart before 1980
  const startDataIndex = data.findIndex(item => {
    return parseInt(item[0], 10) > 1979;
  });

  return (
    <div
      ref={chartContainer}
      className="relative h-2/5-screen-height overflow-hidden leafwell-area-chart"
    >
      <SVGElement />
      <Chart
        chartType="AreaChart"
        data={data}
        options={options}
        formatters={[
          {
            column: 0,
            type: 'NumberFormat' as const,
            options: {
              pattern: '#',
            },
          },
        ]}
        chartPackages={['corechart', 'controls']}
        controls={[
          {
            controlType: 'ChartRangeFilter',
            options: {
              filterColumnIndex: 0,
              ui: {
                chartType: 'AreaChart',
                chartOptions: {
                  ...chartOptions,
                  areaOpacity: 0.8,
                  lineWidth: 1,

                  hAxis: {
                    baselineColor: 'none',
                    format: '#',
                    textPosition: 'out',
                    allowContainerBoundaryTextCutoff: true,
                    gridlines: {
                      color: '#e5e5e5',
                    },
                    minorGridlines: {
                      color: 'none',
                    },
                  },
                  chartArea: {
                    backgroundColor: '#f7f4fc',
                    left: 60,
                    height: '80%',
                    width: width - 80,
                  },
                  height: height * 0.25,
                  width,
                },
                minRangeSize: 2,
              },
            },
            controlPosition: 'bottom',
            controlWrapperParams: {
              state: {
                range: {
                  start: data[startDataIndex][0],
                  end: data[data.length - 1][0],
                },
              },
            },
          },
        ]}
      />
    </div>
  );
};

export default StudiesByYearChart;
